import { A11yModule } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { StopPropagationDirective } from '../../directives/stop-propagation.directive';
import { HxAccordion } from '../accordion/accordion';
import { HxAccordionPanel } from '../accordion/accordion-panel';
import { HxButton } from '../button/button';
import { HxConfirmationPopup } from '../confirmation-popup/confirmation-popup';
import { HxIconButton } from '../icon-button/icon-button';
import { HxIcon } from '../icon/icon';
import { HxSearchTypeAhead } from '../search-typeahead/search-typeahead';
import { type SearchItem, type SearchTypeaheadLoadMoreEvent } from '../search-typeahead/search-typeahead.types';
import { HxSideNav } from '../side-nav/side-nav';
import { HxSnackbarService } from '../snackbar/snackbar-service';
import { HxTagComponent } from '../tag/tag';
import { HxTooltipDirective } from '../tooltip/tooltip';
import { type IcdCode, type IcdLookupSourceGroup, type IcdLookupSourceRef } from './icd-lookup.types';

/**
 * HxIcdLookup Component
 *
 * Search and select ICD-10 codes alongside a grouped, navigable quick view of existing diagnoses.
 *
 * @example
 *   ```typescript
 *   import { HxIcdLookup } from '@complexcare/harmony/icd-lookup';
 *
 *   <hx-icd-lookup
 *     [icdCodes]="availableCodes"
 *     [excludedCodes]="alreadyAddedCodes()"
 *     (codesAdded)="onCodesAdded($event)">
 *   </hx-icd-lookup>
 *   ```;
 */
@Component({
  selector: 'hx-icd-lookup',
  standalone: true,
  imports: [
    CommonModule,
    A11yModule,
    CdkPortal,
    HxAccordion,
    HxAccordionPanel,
    HxButton,
    HxConfirmationPopup,
    HxIcon,
    HxIconButton,
    HxSearchTypeAhead,
    HxSideNav,
    HxTagComponent,
    StopPropagationDirective,
    HxTooltipDirective,
  ],
  templateUrl: './icd-lookup.html',
  styleUrls: ['./icd-lookup-tokens.css', './icd-lookup.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'data-hx-search-activation': '',
    '(hx-search-activate)': 'activateSearch($event)',
    '[class.hx-icd-lookup-control]': 'true',
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class HxIcdLookup implements OnDestroy {
  private static nextId = 0;
  protected readonly panelId = `hx-icd-lookup-${HxIcdLookup.nextId++}`;
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  private readonly overlay = inject(Overlay);
  private readonly panelPortal = viewChild(CdkPortal);
  private overlayRef: OverlayRef | null = null;
  private readonly trigger = viewChild('trigger', { read: ElementRef<HTMLButtonElement> });
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');
  private readonly searchTypeahead = viewChild(HxSearchTypeAhead);
  protected readonly pendingSource = signal<IcdLookupSourceRef | null>(null);

  /** Snackbar service for success notifications */
  private readonly snackbar = inject(HxSnackbarService);

  /** Is the ICD lookup panel currently open */
  protected readonly isPanelOpen = signal<boolean>(false);

  /** Array of available ICD codes to search from */
  readonly icdCodes = input.required<IcdCode[]>();

  /** Optional: ICD codes to exclude from search results (already added codes) */
  readonly excludedCodes = input<IcdCode[]>([]);

  /** Committed codes contributed by section/subsection controls. */
  readonly sourceGroups = input<IcdLookupSourceGroup[]>([]);

  /** Committed global-lookup codes, displayed separately from pending selections. */
  readonly alreadySelectedCodes = input<IcdCode[]>([]);

  readonly goToSource = output<IcdLookupSourceRef>();

  /** Optional: Custom dialog title (default: "ICD Search") */
  readonly title = input<string>('ICD Search');

  /** Optional: Custom search placeholder (default: "Type in ICD code or code description to search") */
  readonly placeholder = input<string>('Type in ICD code or code description to search');

  /** Optional: Button aria-label (default: "Add ICD Codes") */
  readonly buttonAriaLabel = input<string>('Add ICD Codes');

  /** Optional: Icon for button (default: "plus-circle") */
  readonly buttonIcon = input<string>('plus-circle');

  /** Optional: Button variant (default: "fill") */
  readonly buttonVariant = input<'fill' | 'soft' | 'outline' | 'ghost'>('fill');

  /** Optional: Button context/color (default: "brand") */
  readonly buttonContext = input<'brand' | 'neutral' | 'positive' | 'negative' | 'caution' | 'info'>('brand');

  /** Optional: Button size (default: "md") */
  readonly buttonSize = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');

  /** Optional: Whether button is rounded (default: true) */
  readonly buttonRounded = input<boolean>(true);

  /** Optional: Inner icon font-size (e.g. '1em', '20px') */
  readonly iconSize = input<string>('1em');

  /** Optional: Tooltip text for the button (default: undefined - no tooltip) */
  readonly buttonTooltip = input<string | undefined>(undefined);

  /** Optional: Whether the button is disabled (default: false) */
  readonly disabled = input<boolean>(false);

  /** Request fresh results on open when a parent supplies server-side search. */
  readonly externalSearch = input<boolean>(false);

  /** Enables near-bottom incremental loading in search results. */
  readonly enableIncrementalLoading = input<boolean>(false);

  /** Whether more ICD results are available from the data source. */
  readonly hasMore = input<boolean>(false);

  /** Whether an incremental page is currently loading. */
  readonly loadingMore = input<boolean>(false);

  /** Page-size hint forwarded to load-more requests. */
  readonly pageSize = input<number>(10);

  /** Emits newly added codes when user confirms selection (fires on Add button click only) */
  readonly codesAdded = output<IcdCode[]>();

  /** Emits when the user types in the search box (for server-side search) */
  readonly searchChange = output<string>();

  /** Emits when the user scrolls to the end of results and needs the next page. */
  readonly loadMore = output<SearchTypeaheadLoadMoreEvent>();

  /** Available ICD codes for search (formatted for search-typeahead) */
  protected readonly searchItems = signal<SearchItem[]>([]);

  /** Currently selected ICD codes */
  protected readonly selectedCodes = signal<IcdCode[]>([]);

  /** Is "Add" button enabled (true when at least one code is selected) */
  protected readonly isAddButtonEnabled = computed(() => this.selectedCodes().length > 0);

  constructor() {
    effect((onCleanup) => {
      const panel = this.panel()?.nativeElement;
      if (!panel) {
        return;
      }
      const onKeyDown = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape' || this.pendingSource() || this.searchTypeahead()?.isDropdownOpen()) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        this.closePanel();
      };
      // Capture Escape before the typeahead consumes it even when its dropdown is already closed.
      panel.addEventListener('keydown', onKeyDown, true);
      let active = true;
      queueMicrotask(() => {
        if (!active || !panel.isConnected || !this.isPanelOpen() || this.pendingSource()) {
          return;
        }
        const search = this.searchTypeahead();
        if (search) {
          search.focusSearchInput();
        } else {
          panel.focus();
        }
      });
      onCleanup(() => {
        active = false;
        panel.removeEventListener('keydown', onKeyDown, true);
      });
    });

    // Update searchItems only when icdCodes or excludedCodes change (search results from parent).
    // isPanelOpen and selectedCodes are read with untracked so they don't re-trigger this effect;
    // re-triggering on isPanelOpen would repopulate stale results whenever the panel reopens.
    effect(
      () => {
        const icdCodes = this.icdCodes();
        const excluded = this.excludedCodes();

        if (!untracked(() => this.isPanelOpen())) {
          return;
        }

        untracked(() => {
          const excludedSet = new Set(excluded.map((code) => code.code));
          const pending = this.selectedCodes().filter((code) => !excludedSet.has(code.code));
          if (pending.length !== this.selectedCodes().length) {
            this.selectedCodes.set(pending);
            this.searchTypeahead()?.writeValue(
              pending.map((code) => ({
                id: code.code,
                displayText: `${code.code} - ${code.description}`,
                metadata: code,
              }))
            );
          }
        });
        this.searchItems.set(untracked(() => this.createSearchItems(icdCodes, excluded)));
      },
      { allowSignalWrites: true }
    );
  }

  private createSearchItems(icdCodes: IcdCode[], excluded: IcdCode[]): SearchItem[] {
    const excludedSet = new Set(excluded.map((icd) => icd.code));
    const selectedSet = new Set(this.selectedCodes().map((icd) => icd.code));
    return icdCodes
      .filter((icd) => !excludedSet.has(icd.code))
      .map((icd) => ({
        id: icd.code,
        displayText: icd.displayText || (icd.description ? `${icd.code} - ${icd.description}` : icd.code),
        isSelected: selectedSet.has(icd.code),
        metadata: icd,
      }));
  }

  activateSearch(event: Event): void {
    if (this.disabled() || this.pendingSource()) {
      return;
    }
    event.preventDefault();
    if (this.isPanelOpen()) {
      this.searchTypeahead()?.focusSearchInput();
    } else {
      this.openPanel();
    }
  }

  /** Open the ICD lookup side-nav */
  protected openPanel(): void {
    // Prevent opening if disabled
    const portal = this.panelPortal();
    if (this.disabled() || this.isPanelOpen() || !portal) {
      return;
    }

    this.selectedCodes.set([]); // Reset selections on open
    this.searchItems.set(this.externalSearch() ? [] : this.createSearchItems(this.icdCodes(), this.excludedCodes()));
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global().top('0').left('0'),
      width: '100%',
      height: '100%',
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
    this.overlayRef.attach(portal);
    this.isPanelOpen.set(true);
    this.toggleBodyOpenClass(true);
    if (this.externalSearch()) {
      this.searchChange.emit('');
    }
  }

  /** Close the ICD lookup side-nav */
  protected closePanel(restoreFocus = true): void {
    this.searchTypeahead()?.closeSearchSession();
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.isPanelOpen.set(false);
    this.searchItems.set([]);
    this.selectedCodes.set([]);
    this.pendingSource.set(null);
    this.toggleBodyOpenClass(false);
    if (restoreFocus) {
      this.trigger()?.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.toggleBodyOpenClass(false);
  }

  /**
   * Body class is used by global styles to hide underlying top notes and prevent layer conflicts while the ICD sidebar
   * is open.
   */
  private toggleBodyOpenClass(isOpen: boolean): void {
    this.document.body.classList.toggle('hx-icd-lookup-open', isOpen);
  }

  /** Handle selection changes from hx-search-typeahead */
  protected onSelectedItemsChanged(selectedItems: SearchItem[]): void {
    const codes: IcdCode[] = [];
    for (const item of selectedItems) {
      const metadata = item.metadata;
      if (metadata && typeof metadata['code'] === 'string' && typeof metadata['description'] === 'string') {
        codes.push({ code: metadata['code'], description: metadata['description'] });
      }
    }
    this.selectedCodes.set(codes);
  }

  /** Sync items state from hx-search-typeahead */
  protected onItemsChanged(updatedItems: SearchItem[]): void {
    this.searchItems.set(updatedItems);
  }

  /** Confirm selection and emit selected codes */
  protected onAdd(): void {
    const codes = this.selectedCodes();
    this.codesAdded.emit(codes);

    this.closePanel();
  }

  /** Cancel selection and close panel */
  protected onCancel(): void {
    this.closePanel();
  }

  protected requestGoToSource(sourceRef: IcdLookupSourceRef): void {
    if (this.selectedCodes().length > 0) {
      this.pendingSource.set(sourceRef);
      return;
    }
    this.navigateToSource(sourceRef);
  }

  protected confirmNavigation(): void {
    const sourceRef = this.pendingSource();
    if (sourceRef) {
      this.navigateToSource(sourceRef);
    }
  }

  private navigateToSource(sourceRef: IcdLookupSourceRef): void {
    this.closePanel(false);
    // Let the overlay and confirmation focus trap be destroyed before focusing the destination.
    afterNextRender(() => this.goToSource.emit(sourceRef), { injector: this.injector });
  }

  /** Handle clicks on the overlay backdrop to close the panel */
  protected onOverlayClick(event: MouseEvent): void {
    // Only close if clicking directly on the overlay (not on panel content)
    if (event.target === event.currentTarget && !this.pendingSource()) {
      this.closePanel();
    }
  }
}
